/* ==========================================================================
   <MotionProvider> — mount once inside <Router> (see App.js).
   - Lenis smooth scroll synced to GSAP ScrollTrigger (skipped for reduced motion)
   - On every route change: scroll to top (or #hash), re-scan data-attributes
     via initMotion(), refresh ScrollTrigger; the previous scan is reverted.
   - Global: scroll progress bar, custom cursor (fine pointers only), in-page
     anchor links that respect the fixed nav.
   ========================================================================== */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { initMotion } from './core';
import { motion, prefersReducedMotion, hasFinePointer } from './api';

gsap.registerPlugin(ScrollTrigger);

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function useSmoothScroll() {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('motion-js');
    if (prefersReducedMotion()) {
      html.classList.add('reduced-motion');
      return () => html.classList.remove('motion-js', 'reduced-motion');
    }
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    let lenis;
    try {
      lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.3 });
    } catch (e) {
      lenis = null;
    }
    if (!lenis) return () => html.classList.remove('motion-js');
    motion.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (t) => lenis.raf(t * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      motion.lenis = null;
      html.classList.remove('motion-js');
    };
  }, []);
}

function useProgressAndCursor() {
  useEffect(() => {
    const RM = prefersReducedMotion();
    /* progress bar */
    const bar = document.createElement('div');
    bar.className = 'scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    let ticking = false;
    const update = () => {
      ticking = false;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const y = motion.lenis ? motion.lenis.scroll : window.pageYOffset;
      bar.style.transform = `scaleX(${h > 0 ? Math.min(1, Math.max(0, y / h)) : 0})`;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    /* cursor */
    let cursorCleanup = () => {};
    if (!RM && hasFinePointer()) {
      const c = document.createElement('div');
      c.className = 'cursor is-hidden';
      c.setAttribute('aria-hidden', 'true');
      c.innerHTML = '<span class="cursor__ring"></span><span class="cursor__dot"></span><span class="cursor__label"></span>';
      document.body.appendChild(c);
      document.documentElement.classList.add('has-cursor');
      const label = c.querySelector('.cursor__label');
      let x = -100; let y = -100; let rx = -100; let ry = -100; let raf;
      const move = (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        x = e.clientX; y = e.clientY;
        c.classList.remove('is-hidden');
      };
      const down = () => c.classList.add('is-down');
      const up = () => c.classList.remove('is-down');
      const leave = () => c.classList.add('is-hidden');
      const over = (e) => {
        const t = e.target.closest ? e.target.closest('[data-cursor]') : null;
        if (t) {
          label.textContent = t.getAttribute('data-cursor') || 'View';
          c.classList.add('is-label');
          c.classList.remove('is-link');
          return;
        }
        c.classList.remove('is-label');
        const l = e.target.closest ? e.target.closest('a, button, [role="button"], label, summary, input, select, textarea') : null;
        c.classList.toggle('is-link', !!l);
      };
      const loop = () => {
        rx += (x - rx) * 0.18; ry += (y - ry) * 0.18;
        c.style.setProperty('--x', `${x}px`); c.style.setProperty('--y', `${y}px`);
        c.style.setProperty('--rx', `${rx.toFixed(1)}px`); c.style.setProperty('--ry', `${ry.toFixed(1)}px`);
        raf = requestAnimationFrame(loop);
      };
      document.addEventListener('pointermove', move, { passive: true });
      document.addEventListener('pointerdown', down);
      document.addEventListener('pointerup', up);
      document.documentElement.addEventListener('mouseleave', leave);
      document.addEventListener('mouseover', over);
      raf = requestAnimationFrame(loop);
      cursorCleanup = () => {
        cancelAnimationFrame(raf);
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerdown', down);
        document.removeEventListener('pointerup', up);
        document.documentElement.removeEventListener('mouseleave', leave);
        document.removeEventListener('mouseover', over);
        document.documentElement.classList.remove('has-cursor');
        c.remove();
      };
    }

    /* in-page anchors (#id on the same route) — offset for the fixed nav */
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = e.target.closest && e.target.closest('a[href*="#"]');
      if (!a || a.target === '_blank') return;
      let url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash || url.hash === '#') return;
      const t = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!t) return;
      e.preventDefault();
      motion.scrollTo(t);
      window.history.pushState(null, '', url.hash);
      if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '-1');
      t.focus({ preventScroll: true });
    };
    document.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('click', onClick);
      bar.remove();
      cursorCleanup();
    };
  }, []);
}

export default function MotionProvider({ children }) {
  const { pathname, hash } = useLocation();
  const first = useRef(true);

  useSmoothScroll();
  useProgressAndCursor();

  // Scroll to top / hash on navigation (before paint to avoid a flash).
  useIsoLayoutEffect(() => {
    if (first.current && !hash) { first.current = false; return; }
    first.current = false;
    if (!hash) {
      if (motion.lenis) motion.lenis.scrollTo(0, { immediate: true, force: true });
      window.scrollTo(0, 0);
      // ScrollTrigger caches the window scroll position, and a refresh restores
      // that cached value. Set it through ScrollTrigger's own scroll function so
      // the refresh after a route change doesn't jump back to the old page's y.
      try { ScrollTrigger.getScrollFunc(window)(0); } catch (e) { /* noop */ }
    }
  }, [pathname]);

  // Re-scan the page for motion attributes on every route.
  useIsoLayoutEffect(() => {
    const root = document.querySelector('.App') || document.body;
    const revert = initMotion(root);
    let hashTimer;
    const raf = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
      if (hash) {
        hashTimer = setTimeout(() => {
          const t = document.getElementById(decodeURIComponent(hash.slice(1)));
          if (t) motion.scrollTo(t, { immediate: true });
        }, 60);
      }
    });
    // refresh triggers when late content (images, fonts, data) changes height
    let rt;
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => { clearTimeout(rt); rt = setTimeout(() => ScrollTrigger.refresh(), 200); })
      : null;
    if (ro) ro.observe(document.body);
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener('load', onLoad);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh()).catch(() => {});

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hashTimer);
      clearTimeout(rt);
      if (ro) ro.disconnect();
      window.removeEventListener('load', onLoad);
      revert();
    };
  }, [pathname]);

  return children || null;
}
